import React, {useState} from 'react';

import {CHEMICAL_EXTENSIONS, EMPTY_MRV, REACTION_EXTENSIONS} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
  useControlledChemicalStructureDrawer,
  useReactionDetection,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';

import ChemicalSketcherModalInputFields from './ChemicalSketcherModalInputFields';
import {molConvert} from './MoleculeDAO';

export interface ChemicalSketcherModalReturnParams {
  sketcherValue: string;
  filename: string;
  saveFormat: MarvinChemicalFormat;
}
export interface ChemicalSketcherModalProps {
  initialChemicalStructure?: ChemicalStructureDrawerValue;
  initialFilename?: string;
  inputFormat?: MarvinChemicalFormat;
  onRequestClose: () => void;
  onRequestSubmit: (returnParams: ChemicalSketcherModalReturnParams) => void;
  showAttachmentPointBanner?: boolean;
}

export default function ChemicalSketcherModal({
  initialChemicalStructure = EMPTY_MRV,
  initialFilename = '',
  inputFormat = MarvinChemicalFormat.MRV,
  onRequestClose,
  onRequestSubmit,
  showAttachmentPointBanner = true,
}: ChemicalSketcherModalProps): JSX.Element {
  const [initialSketcherValue, sketcherValue, setSketcherValue] =
    useControlledChemicalStructureDrawer(initialChemicalStructure);
  const [isReaction, setIsReaction] = useReactionDetection();
  const [saving, setSaving] = useState(false);
  const [filename, setFilename] = useState<string>(initialFilename);
  const [saveFormat, setSaveFormat] = useState<MarvinChemicalFormat>(inputFormat);
  const modalTitle = initialFilename ? 'Edit chemical file' : 'New chemical file';

  const structureUnchanged = sketcherValue === initialSketcherValue;
  const filenameUnchanged = filename === initialFilename;
  const formatUnchanged = saveFormat === inputFormat;

  const validExtension = isReaction
    ? REACTION_EXTENSIONS.includes(saveFormat)
    : CHEMICAL_EXTENSIONS.includes(saveFormat);
  const inputFieldError = filename.trim().length === 0 || !validExtension;
  const disabled = (structureUnchanged && filenameUnchanged && formatUnchanged) || inputFieldError || saving;

  async function updateChemicalStructure(): Promise<void> {
    if (sketcherValue != null) {
      setSaving(true);
      try {
        const convertedOutput = await molConvert(null, saveFormat, sketcherValue);
        const returnParams: ChemicalSketcherModalReturnParams = {
          sketcherValue: convertedOutput.structure ?? convertedOutput.binaryStructure,
          filename,
          saveFormat,
        };
        onRequestSubmit(returnParams);
      } catch (err) {
        setSaving(false);
        throw new Error('Unable to save new structure.');
      }
    }
  }

  return (
    <ExpandableModal
      onRequestClose={onRequestClose}
      onRequestSubmit={updateChemicalStructure}
      startMaximized={true}
      title={modalTitle}
      buttons={[{disabled, submit: true, text: 'Save'}]}
    >
      <ChemicalSketcherModalInputFields
        filename={filename}
        setFilename={setFilename}
        saveFormat={saveFormat}
        setSaveFormat={setSaveFormat}
        isReaction={isReaction}
      />
      <ChemicalStructureModalInput
        initialSketcherValue={initialSketcherValue}
        onSketcherValueChange={setSketcherValue}
        setHasReactionContents={setIsReaction}
        mode={ChemicalStructureDrawerMode.REACTION}
        inputFormat={null}
        outputFormat={MarvinChemicalFormat.MRV}
        showAttachmentPointBanner={showAttachmentPointBanner}
      />
    </ExpandableModal>
  );
}
